import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Auth } from 'aws-amplify';

const Signup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [eventData, setEventData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const eventId = searchParams.get('eventId');
  
  const [formData, setFormData] = useState({
    eventName: '',
    firstName: '',
    lastName: '',
    email: searchParams.get('email') || '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const loadEventData = async () => {
      if (!eventId) return;
      try {
        const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}`);
        const event = await response.json();
        setEventData(event);
        setFormData(prev => ({ ...prev, eventName: event.name || '', email: event.clientEmail || prev.email }));
        
        if (event.primaryVendorId) {
          const vendorResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${event.primaryVendorId}`);
          const vendor = await vendorResponse.json();
          setVendorData(vendor);
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      } finally {
        setLoading(false);
      }
    };
    loadEventData();
  }, [eventId]);

  const handleAcceptInvite = () => {
    // If there's a contract or quote, show review step first
    if (billingModel === 'Contract' || billingModel === 'Quote') {
      setStep(2);
    } else {
      setStep(3);
    }
  };
  
  const handleAccountSetup = async () => {
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setSubmitting(true);
    try {
      // Create new user via backend
      const signupResponse = await fetch('https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/users/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          eventId
        })
      });
      
      if (!signupResponse.ok) {
        const errorText = await signupResponse.text();
        console.error('Signup failed:', signupResponse.status, errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error(`Server error: ${errorText}`);
        }
        throw new Error(errorData.message || errorData.error || 'Failed to create account');
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      const cognitoUser = await Auth.signIn(formData.email, formData.password);
      
      // Set user's currentEventId
      try {
        await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/users/${cognitoUser.username}/current-event`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventId })
        });
      } catch (err) {
        console.error('Failed to set current event:', err);
      }
      
      // Get or create the Venue category
      let venueCategoryId = null;
      try {
        const catResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}/categories`);
        if (catResponse.ok) {
          const categories = await catResponse.json();
          const venueCategory = categories.find(cat => cat.name === 'Venue');
          if (venueCategory) {
            venueCategoryId = venueCategory.id;
          } else {
            // Create Venue category if it doesn't exist
            const createCatResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventId}/categories`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                name: 'Venue',
                icon: 'venue',
                budget: eventData?.budget || 0
              })
            });
            if (createCatResponse.ok) {
              const newCategory = await createCatResponse.json();
              venueCategoryId = newCategory.id;
            }
          }
        }
      } catch (err) {
        console.error('Failed to get/create venue category:', err);
      }
      
      // Create individual quotes if billing model is Quote
      if (billingModel === 'Quote' && quoteItems.length > 0 && venueCategoryId) {
        const enhancedApiClient = (await import('../api/enhancedApiClient')).default;
        for (const item of quoteItems) {
          if (item.item && item.amount) {
            const quote = {
              eventId: eventId,
              id: `quote-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              invoiceType: 'quote',
              vendorId: eventData?.primaryVendorId || '',
              vendorName: vendorData?.name?.S || vendorData?.name || '',
              categoryId: venueCategoryId,
              name: item.item,
              description: item.item,
              depositAmount: parseFloat(item.amount) || 0,
              amount: parseFloat(item.amount) || 0,
              units: parseInt(item.units) || 1,
              status: 'pending',
              createdAt: new Date().toISOString()
            };
            await enhancedApiClient.createQuote(quote);
          }
        }
      }
      
      // Create deposit invoice if deposit amount is provided
      if (depositAmount && venueCategoryId) {
        const enhancedApiClient = (await import('../api/enhancedApiClient')).default;
        const invoice = {
          eventId: eventId,
          id: `invoice-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          invoiceType: 'invoice',
          vendorId: eventData?.primaryVendorId || '',
          vendorName: vendorData?.name?.S || vendorData?.name || '',
          categoryId: venueCategoryId,
          name: 'Deposit',
          description: 'Deposit',
          depositAmount: parseFloat(depositAmount) || 0,
          amount: parseFloat(depositAmount) || 0,
          dueDate: depositDue || 'Due on acceptance',
          status: 'pending',
          createdAt: new Date().toISOString()
        };
        await enhancedApiClient.createInvoice(invoice);
      }
      
      // Send credentials email
      try {
        await fetch('https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/send-credentials', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: formData.email,
            name: `${formData.firstName} ${formData.lastName}`,
            password: formData.password,
            eventName: eventData?.name || formData.eventName
          })
        });
      } catch (emailError) {
        console.log('Email send failed, continuing...', emailError);
      }
      
      setStep(4);
    } catch (err) {
      setError(err.message || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleAcceptContractOrQuote = () => setStep(3);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>;

  const vendorName = vendorData?.name?.S || vendorData?.name || 'Orakei Bay';
  const eventDate = eventData?.eventDate || '10 December 2025';
  const eventName = eventData?.name || formData.eventName || 'Sam & Taylor';
  const billingModel = eventData?.billingModel || 'None';
  const depositAmount = eventData?.depositAmount || '';
  const depositDue = eventData?.depositDue || 'Due on acceptance';
  const contractUrl = eventData?.contractUrl || '';
  const quoteItems = eventData?.quoteItems || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', padding: '20px 48px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '28px', fontWeight: '400', color: '#6B9080', fontFamily: 'Georgia, serif' }}>everset</div>
        <div style={{ color: '#6B7280', fontSize: '14px' }}>Help</div>
      </div>
      
      {/* Event Header */}
      <div style={{ backgroundColor: '#E8F4E8', padding: '32px 48px', textAlign: 'center' }}>
        <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '8px' }}>{eventDate} · {vendorName}</div>
        <h1 style={{ fontSize: '56px', fontWeight: '400', color: '#1F2937', margin: 0, fontFamily: 'Georgia, serif' }}>{eventName}</h1>
      </div>
      
      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '48px auto', padding: '0 48px', display: 'grid', gridTemplateColumns: '400px 1fr', gap: '48px' }}>
        {/* Left Sidebar */}
        <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '12px', height: 'fit-content', border: '1px solid #E5E7EB' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1F2937', marginBottom: '16px', fontFamily: 'Georgia, serif' }}>Welcome to Everset</h2>
          <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', marginBottom: '16px' }}>
            Everset is a shared space where couples and vendors keep wedding plans organised. Everything sits in one place so you can see what's booked, what's coming up and what needs your attention.
          </p>
          <p style={{ color: '#6B7280', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
            Once you accept your invite, you'll be able to review details from your vendor or venue, manage payments and stay connected as your day takes shape.
          </p>
          <a href="https://everset.co.nz" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280', fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>
            Learn more about Everset <span style={{ fontSize: '20px' }}>→</span>
          </a>
        </div>
        
        {/* Right Content */}
        {step === 1 && (
          <div style={{ padding: '48px', borderRadius: '12px', backgroundImage: 'url(https://images.unsplash.com/photo-1519167758481-83f29da8c2b0?w=800)', backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)' }}></div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ fontSize: '48px', fontWeight: '400', color: 'white', marginBottom: '24px', fontFamily: 'Georgia, serif' }}>Welcome</h2>
              <p style={{ color: 'white', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
                Hi {eventData?.clientName || 'there'}, Thanks for your interest in holding your wedding at {vendorName} on {eventDate}. Please accept our invite to Everset so we can help plan your wedding with you.
              </p>
              {eventData?.message && (
                <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', padding: '16px', borderRadius: '8px', marginBottom: '32px', borderLeft: '3px solid white' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px', fontWeight: '500' }}>Message from {vendorName}</div>
                  <p style={{ color: 'white', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>{eventData.message}</p>
                </div>
              )}
              <button onClick={handleAcceptInvite} style={{ width: '100%', padding: '16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}>
                Accept Invite
              </button>
            </div>
          </div>
        )}
        
        {step === 2 && billingModel === 'Contract' && (
          <div style={{ backgroundColor: 'white', padding: '48px', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
            <h2 style={{ fontSize: '32px', fontWeight: '400', color: '#1F2937', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>Review Contract</h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '32px' }}>
              Please review the contract from {vendorName}. Once you're happy, click "Accept Contract" to proceed with account setup.
            </p>
            
            {contractUrl ? (
              <div style={{ backgroundColor: '#F9FAFB', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '24px' }}>
                <iframe
                  src={contractUrl}
                  style={{ width: '100%', height: '500px', border: 'none', borderRadius: '8px' }}
                  title="Contract Document"
                />
              </div>
            ) : (
              <div style={{ backgroundColor: '#F9FAFB', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '24px', textAlign: 'center', color: '#6B7280' }}>
                No contract document available
              </div>
            )}
            
            {depositAmount && (
              <div style={{ backgroundColor: '#F9FAFB', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '56px', height: '56px', backgroundColor: '#E8F4E8', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>💰</div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '4px' }}>${depositAmount} Deposit Required</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>{depositDue}</div>
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <button onClick={() => setStep(1)} style={{ padding: '16px', backgroundColor: '#E8F4E8', color: '#1F2937', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}>Back</button>
              <button onClick={handleAcceptContractOrQuote} style={{ padding: '16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}>Accept Contract</button>
            </div>
          </div>
        )}
        
        {step === 2 && billingModel === 'Quote' && (
          <div style={{ backgroundColor: 'white', padding: '48px', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
            <h2 style={{ fontSize: '32px', fontWeight: '400', color: '#1F2937', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>Review Quote</h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '32px' }}>
              Please review the quote from {vendorName}. Once you're happy, click "Accept Quote" to proceed with account setup.
            </p>
            
            {quoteItems.length > 0 && (
              <div style={{ backgroundColor: '#F9FAFB', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '24px' }}>
                <div style={{ fontSize: '16px', fontWeight: '500', color: '#1F2937', marginBottom: '16px' }}>Quote Items</div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>ITEM</div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>UNITS</div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280' }}>AMOUNT</div>
                </div>
                {quoteItems.map((item, index) => (
                  <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', padding: '12px 0', borderBottom: index < quoteItems.length - 1 ? '1px solid #E5E7EB' : 'none' }}>
                    <div style={{ fontSize: '14px', color: '#1F2937' }}>{item.item}</div>
                    <div style={{ fontSize: '14px', color: '#1F2937' }}>{item.units}</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937' }}>${parseFloat(item.amount || 0).toLocaleString()}</div>
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px', paddingTop: '16px', marginTop: '16px', borderTop: '2px solid #E5E7EB' }}>
                  <div></div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>Total</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>${quoteItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toLocaleString()}</div>
                </div>
              </div>
            )}
            
            {depositAmount && (
              <div style={{ backgroundColor: '#F9FAFB', padding: '24px', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '32px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '56px', height: '56px', backgroundColor: '#E8F4E8', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>💰</div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '500', color: '#1F2937', marginBottom: '4px' }}>${depositAmount} Deposit Required</div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>{depositDue}</div>
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <button onClick={() => setStep(1)} style={{ padding: '16px', backgroundColor: '#E8F4E8', color: '#1F2937', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}>Back</button>
              <button onClick={handleAcceptContractOrQuote} style={{ padding: '16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}>Accept Quote</button>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div style={{ backgroundColor: 'white', padding: '48px', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
            <h2 style={{ fontSize: '32px', fontWeight: '600', color: '#1F2937', marginBottom: '32px', fontFamily: 'Georgia, serif' }}>Account Setup</h2>
            {error && <div style={{ backgroundColor: '#FEE2E2', color: '#991B1B', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Event Name</label>
              <input type="text" value={formData.eventName} onChange={(e) => setFormData({...formData, eventName: e.target.value})} disabled={false} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '16px', backgroundColor: '#F9FAFB' }} />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>First Name</label>
                <input type="text" required value={formData.firstName} onChange={(e) => setFormData({...formData, firstName: e.target.value})} disabled={false} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '16px', backgroundColor: '#F9FAFB' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Last Name</label>
                <input type="text" required value={formData.lastName} onChange={(e) => setFormData({...formData, lastName: e.target.value})} disabled={false} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '16px', backgroundColor: '#F9FAFB' }} />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Email</label>
                <input type="email" required value={formData.email} disabled={false} readOnly style={{ width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '16px', backgroundColor: '#F3F4F6' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Phone</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} disabled={false} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '16px', backgroundColor: '#F9FAFB' }} />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Password</label>
                <input type="password" required value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} disabled={false} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '16px', backgroundColor: '#F9FAFB' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>Confirm Password</label>
                <input type="password" required value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} disabled={false} style={{ width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '16px', backgroundColor: '#F9FAFB' }} />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <button onClick={() => billingModel === 'None' ? setStep(1) : setStep(2)} disabled={submitting} style={{ padding: '16px', backgroundColor: '#E8F4E8', color: '#1F2937', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.5 : 1 }}>Back</button>
              <button onClick={handleAccountSetup} disabled={submitting} style={{ padding: '16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>{submitting ? 'Creating Account...' : 'Sign Up'}</button>
            </div>
          </div>
        )}
        
        {step === 4 && (
          <div style={{ padding: '48px', borderRadius: '12px', backgroundImage: 'url(https://images.unsplash.com/photo-1519167758481-83f29da8c2b0?w=800)', backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)' }}></div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ fontSize: '48px', fontWeight: '400', color: 'white', marginBottom: '24px', fontFamily: 'Georgia, serif' }}>You're all set!</h2>
              <p style={{ color: 'white', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>
                Thanks {formData.firstName}, your account has been created. We've sent your login details to {formData.email}. You can now sign in to access your event dashboard.
              </p>
              <button onClick={() => window.location.href = 'https://3ug.co.nz'} style={{ width: '100%', padding: '16px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}>
                Go to Sign In
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
